import type { GeoPt } from './types'

/**
 * The CRS a print sheet is composed in, exactly as the print server's probe
 * describes it. The server's `crs` (`"EPSG:3067"`) parsed to its code, its
 * `proj4` definition verbatim, and `ground_m_per_crs_m`: how many ground metres
 * one CRS metre is at the probe point — 1 for a national grid, cos(latitude)
 * for the Web Mercator fallback.
 *
 * There is no table of projections here any more, and no special case for
 * Mercator: the day the print server gains a national grid for a new country,
 * the web draws it correctly without a release, because it never knew the old
 * list. That is the whole reason the probe exists, and a local table beside it
 * would be the coupling it was built to remove.
 */
export interface PrintCrs {
  epsg: number
  proj4: string
  groundMPerCrsM: number
}

export interface PrintProjector {
  forward(p: GeoPt): [number, number]
  inverse(xy: [number, number]): GeoPt
  /**
   * How many of this CRS's metres one metre of ground measures.
   *
   * Every print quantity that starts life as ground distance — the sheet's
   * extent, its clip rectangle, the simplification tolerance — has to be
   * multiplied by this before it can be laid out in the projected plane. For a
   * national grid the factor is 1 and the conversion is invisible, which is
   * exactly why it has to be explicit.
   *
   * The latitude argument is kept on the signature and ignored: the factor is
   * the server's, evaluated at the probe point, and taking it from there rather
   * than recomputing cos(lat) here is what keeps the frame on screen and the
   * sheet the server composes on one number. The probe point and a caller's
   * view centre share a 0.01° cell, which at 60°N moves cos by ~0.015%.
   */
  crsMetresPerGroundMetre(lat: number): number
}

type Proj4Converter = {
  forward(xy: [number, number]): [number, number]
  inverse(xy: [number, number]): [number, number]
}

// Keyed on epsg + the proj4 definition string, not epsg alone: `proj4.defs`
// only runs on the first load for a given key, below, so two probes naming
// the same EPSG code with different proj4 strings — a plausible probe change,
// since the code is the print server's short name and the string is its
// actual definition — would otherwise silently keep whichever definition
// loaded first for the rest of the session.
const converterCache = new Map<string, Promise<Proj4Converter>>()

/**
 * The proj4 converter for a CRS, loaded once per distinct (epsg, proj4 string)
 * pair.
 *
 * `proj4` is loaded via a dynamic `import()` so it lands in its own chunk: most
 * sessions never open a print surface, and bundling a projection library into
 * the main chunk would tax every visit for a feature most of them don't touch.
 * A failed load is evicted so the next call retries instead of handing out the
 * cached rejection — the hashed chunk this build asks for is gone after a
 * redeploy, and a reload fixes that, but only if we ask again.
 */
function loadConverter(crs: PrintCrs): Promise<Proj4Converter> {
  const key = `${crs.epsg}|${crs.proj4}`
  const cached = converterCache.get(key)
  if (cached) return cached

  const promise = (async (): Promise<Proj4Converter> => {
    const proj4Module = await import('proj4')
    const proj4 = proj4Module.default
    const name = `EPSG:${crs.epsg}`
    proj4.defs(name, crs.proj4)
    return proj4(name)
  })()

  converterCache.set(key, promise)
  promise.catch(() => {
    if (converterCache.get(key) === promise) converterCache.delete(key)
  })
  return promise
}

/**
 * A forward/inverse pair for the probe's CRS, carrying the probe's own ground
 * factor.
 *
 * The converter is cached per EPSG code; the projector object is not, because
 * the ground factor belongs to the probe point and two sheets on the same
 * Mercator fallback at different latitudes must not share one.
 */
export async function loadPrintProjector(crs: PrintCrs): Promise<PrintProjector> {
  const converter = await loadConverter(crs)
  const factor = 1 / crs.groundMPerCrsM
  return {
    forward: (p: GeoPt): [number, number] => {
      const [x, y] = converter.forward([p.lon, p.lat])
      return [x, y]
    },
    inverse: ([x, y]: [number, number]): GeoPt => {
      const [lon, lat] = converter.inverse([x, y])
      return { lat, lon }
    },
    crsMetresPerGroundMetre: () => factor,
  }
}
